import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { PageIntro } from "@/components/layout/PageIntro";
import api from "@/lib/api";
import { useMall } from "@/context/MallContext";

const BLOCKS = [
    ["Shop policies", "policies", "store-policies"],
    ["Pickup instructions", "pickup_instructions", "store-pickup-instructions"],
    ["Turnaround", "turnaround", "store-turnaround"],
];

/** /stores/:slug/policies — the rules, off the sales flow and linked from the footer. */
export default function StorePolicies() {
    const { slug } = useParams();
    const { enter } = useMall();
    const [store, setStore] = useState(null);
    const [err, setErr] = useState(false);

    useEffect(() => {
        api.get(`/stores/site/${slug}`).then(({ data }) => {
            setStore(data.store);
            enter({ ...data.store, designers: data.store.designers ?? data.store.designers_enabled }, "store");
        }).catch(() => setErr(true));
    }, [slug, enter]);

    if (err) return <Layout><p className="py-24 text-center text-zinc-600" data-testid="store-missing">That shop is not in the directory.</p></Layout>;
    if (!store) return <Layout><div className="flex justify-center py-32"><Loader2 className="animate-spin text-zinc-400" /></div></Layout>;

    const ops = store.ops || {};
    const blocks = BLOCKS.map(([title, key, testid]) => [title, store[key], testid]).filter(([, v]) => v);
    return (
        <Layout>
            <PageIntro trail={[{ label: store.name, to: `/stores/${slug}` }, { label: "Shop policies" }]}
                kicker={`${store.name} · ${store.city || ""}`} title="Shop policies." testid="policies-intro"
                description={`How ${store.name} handles pickup, turnaround, your own garments and refunds.`}>
                <Link to={`/stores/${slug}`} data-testid="policies-back" className="inline-flex items-center gap-1.5 text-sm font-bold text-zinc-700 hover:text-zinc-900">
                    <ArrowLeft size={14} /> Back to {store.name}
                </Link>
            </PageIntro>
            <section className="mx-auto max-w-3xl px-4 py-10 sm:px-6" data-testid="policies-page">
                {blocks.length === 0 && (
                    <p className="text-sm text-zinc-600" data-testid="store-policies-empty">{store.name} has not posted shop policies yet. Message the shop with any question.</p>
                )}
                {blocks.map(([title, body, testid]) => (
                    <div key={testid} className="mb-8 border border-zinc-200 bg-zinc-50 p-6">
                        <h2 className="font-display text-lg font-bold text-zinc-900">{title}</h2>
                        <p className="mt-2 whitespace-pre-line text-sm text-zinc-600" data-testid={testid}>{body}</p>
                    </div>
                ))}
                {(ops.address || store.hours) && (
                    <p className="text-xs text-zinc-500" data-testid="policies-contact">
                        {ops.address || store.pickup_address}{store.hours ? ` · ${store.hours}` : ""}
                    </p>
                )}
            </section>
        </Layout>
    );
}
